/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";
import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { VisibilityOff, Visibility, BorderRight } from "@mui/icons-material";
import {
  Backdrop,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Button,
  Tab,
  Tabs,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OtpVerifyModal from "@/modal/optVerifyModal";
import ResetPassword from "@/modal/resetPassword";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isValidInput } from "@/utils/validations";
import {
  useLoginMutation,
  useLoginWithPhoneMutation,
  useVerifyOtpMutation,
  useResendOtpMutation
} from "@/service/auth";
import toast from "react-hot-toast";
import { ONBOARDING_TYPE } from "@/constants/constants";
import { useAppDispatch } from "@/lib/hook";
import { setToken } from "@/lib/slices/authSlice";
import { getFromStorage, setToStorage, removeFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Helper to allow use of separateDialCode prop without TypeScript errors
const PhoneInputAny: any = PhoneInput;

const Login = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const [open1, setOpen1] = useState(false);
  const handleCloseModal1 = () => {
    setOpen1(false);
  };
  const [open2, setOpen2] = useState(false);
  const handleCloseModal2 = () => {
    setOpen2(false);
  };

  const [login, { isLoading }] = useLoginMutation();
  const [loginWithPhone, { isLoading: isPhoneLoggingIn }] =
    useLoginWithPhoneMutation();
  const [verifyOtp, { isLoading: isOtpVerifying }] = useVerifyOtpMutation();
  const [resendOtp] = useResendOtpMutation();

  const [phone, setPhone] = useState<string>("");
  const [phoneCode, setPhoneCode] = useState<string>("91");
  const [phoneCountry, setPhoneCountry] = useState<string>("");

  // OTP related state for phone login
  const [otp, setOtp] = useState<string>("");
  const [countDown, setCountDown] = useState<number>(59);
  const [phoneLoginUserId, setPhoneLoginUserId] = useState<number | null>(null);
  const isBlockingAction = isLoading || isPhoneLoggingIn || isOtpVerifying;

  // Load remembered credentials on component mount (only for email login)
  const loadRememberedCredentials = () => {
    const rememberedData = getFromStorage(STORAGE_KEYS.rememberedCredentials);
    if (rememberedData) {
      try {
        const parsed = JSON.parse(rememberedData);
        // Only load email credentials, not phone (remember me disabled for phone)
        if (parsed.email) {
          return {
            email: String(parsed.email).trimEnd(),
            password: parsed.password || "",
            rememberMe: true
          };
        }
        // If phone data exists in storage, clear it (remember me not supported for phone)
        if (parsed.phone) {
          removeFromStorage(STORAGE_KEYS.rememberedCredentials);
        }
      } catch (error) {
        // If parsing fails, clear the corrupted data
        removeFromStorage(STORAGE_KEYS.rememberedCredentials);
      }
    }
    return { email: "", password: "", rememberMe: false };
  };

  const rememberedCreds = loadRememberedCredentials();
  const [rememberMe, setRememberMe] = useState<boolean>(rememberedCreds.rememberMe);

  const formik = useFormik({
    initialValues: {
      email: rememberedCreds.email,
      password: rememberedCreds.password,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .transform((value) =>
          typeof value === "string" ? value.trimEnd() : value
        )
        .required("Email is required")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter a valid email address"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken) || "web";
      const normalizedEmail =
        typeof values.email === "string" ? values.email.trimEnd() : values.email;
      const body = {
        email: normalizedEmail,
        password: values.password,
        fcm_token: fcmToken,
      };
      try {
        const res: any = await login({ body }).unwrap();

        // Extract token from login response
        const token =
          (res?.data as any)?.auth_token ||
          (res?.data as any)?.token ||
          (res?.data as any)?.access_token ||
          (res as any)?.token;

        const nodeToken = (res?.data as any)?.jwt_token || "";

        if (token) {
          dispatch(setToken({ token }));
          setToStorage(STORAGE_KEYS.token, token);
          setToStorage(STORAGE_KEYS.tokenNode, nodeToken);
        }

        // Handle remember me functionality - save email and password
        if (rememberMe) {
          const rememberedData = {
            email: normalizedEmail,
            password: values.password,
          };
          setToStorage(STORAGE_KEYS.rememberedCredentials, JSON.stringify(rememberedData));
        } else {
          // Clear remembered credentials if remember me is unchecked
          removeFromStorage(STORAGE_KEYS.rememberedCredentials);
        }

        // Persist full user data so profile screens can use it
        if (res?.data) {
          try {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(res.data));
            // Dispatch custom event to notify Header component of credentials update
            if (typeof window !== "undefined") {
              window.dispatchEvent(new Event('credentialsUpdated'));
            }
          } catch {
            // ignore JSON/stringify issues
          }
        }

        toast.success(res?.message || "Logged in successfully");
        // Navigate based on profile_setup status
        // profile_setup = 0 means incomplete profile, navigate to profile setup
        // profile_setup = 1 means profile completed, navigate to home
        const profileSetup = res?.data?.profile_setup;
        if (profileSetup === 0) {
          router.push("/auth/profile-setup");
        } else {
          router.push("/");
        }
      } catch (error: any) {
        toast.error(error?.data?.message || "Login failed");
      }
    },
  });

  return (
    <>
      <Backdrop
        open={isBlockingAction}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div className="rt_inner login_inner">
        <div className="auth_head  ">
          <h2>Sign in to your Account</h2>
          <p>
            Don’t have an account?{" "}
            <a
              className="text_btn"
              onClick={() => router.push("/auth/sign-up")}
            >
              {" "}
              Create account
            </a>
          </p>
        </div>
        <form className="form whiteBox p_0" onSubmit={formik.handleSubmit}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            className="site_tabs1 v2"
          >
            <Tab label="Email" {...a11yProps(0)} />
            <Tab label="Phone" {...a11yProps(1)} />
          </Tabs>

          <CustomTabPanel index={0} value={value}>
            <div className="tab_content">
              <div className="gap_m">
                <div className="control_group w_100">
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Enter your email"
                    name="email"
                    value={formik.values.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isValidInput(value)) {
                        formik.setFieldValue("email", value.trimEnd());
                      }
                    }}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email ? String(formik.errors.email) : ""}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="start">
                            <img src="/images/email_icon.svg" alt="icon" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  ></TextField>
                </div>
                <div className="control_group w_100">
                  <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Enter password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formik.values.password}
                    onChange={(e) => {
                      if (isValidInput(e.target.value)) {
                        formik.handleChange(e);
                      }
                    }}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password ? String(formik.errors.password) : ""
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src="/images/lock_icon.svg" alt="icon" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword
                                  ? "hide the password"
                                  : "display the password"
                              }
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              onMouseUp={handleMouseUpPassword}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </CustomTabPanel>
          <CustomTabPanel index={1} value={value}>
            <div className="tab_content">
              <div className="gap_m">
                <div className="control_group w_100">
                  <label>Phone</label>
                  {/* <PhoneInputAny
                    country={"in"}
                    value={phone}
                    onChange={(
                      value: string,
                      data: { dialCode?: string; countryCode?: string }
                    ) => {
                      const dial = data?.dialCode || "";
                      const localNumber = dial
                        ? value.replace(new RegExp(`^\\+?${dial}`), "").trim()
                        : value;
                      setPhone(localNumber);

                      if (dial) {
                        setPhoneCode(dial);
                      }
                      if (data?.countryCode) {
                        setPhoneCountry(data.countryCode.toUpperCase());
                      }
                    }}
                    // show flag + country code in a separate left segment
                    separateDialCode
                    prefix=""
                    containerStyle={{ width: "100%" }}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderRadius: "12px",
                      border: "1px solid #E4E7EC",
                      paddingLeft: "70px !important",
                      borderLeft: "none",
                      fontSize: "14px",
                    }}
                    buttonStyle={{
                      borderTopLeftRadius: "12px",
                      borderBottomLeftRadius: "12px",
                      border: "1px solid #E4E7EC",
                      backgroundColor: "transparent",
                      borderRight: "none"
                    }}
                  /> */}

                  <div style={{ display: "flex", gap: "0", width: "100%" }}>
                    {/* Country Dropdown Button - Flag + Dropdown Icon Only */}
                    <div
                      style={{
                        position: "relative",
                        minWidth: "70px",
                      }}
                    >
                      <PhoneInputAny
                        country={phoneCountry ? phoneCountry.toLowerCase() : "in"}
                        value={phoneCode && phone ? `+${phoneCode}${phone}` : `+${phoneCode || "91"}`}
                        onChange={(
                          value: string,
                          data: { dialCode?: string; countryCode?: string }
                        ) => {
                          const dial = data?.dialCode || "";
                          const localNumber = dial
                            ? value.replace(new RegExp(`^\\+?${dial}`), "").trim()
                            : value;
                          setPhone(localNumber);

                          if (dial) {
                            setPhoneCode(dial);
                          }
                          if (data?.countryCode) {
                            setPhoneCountry(data.countryCode.toUpperCase());
                          }
                        }}
                        disableCountryGuess
                        separateDialCode={false}
                        prefix=""
                        containerStyle={{ width: "100%" }}
                        inputStyle={{
                          display: "none",
                        }}
                        buttonStyle={{
                          borderTopLeftRadius: "12px",
                          borderBottomLeftRadius: "12px",
                          border: "1px solid #E4E7EC",
                          borderRight: "none",
                          backgroundColor: "#F9FAFB",
                          padding: "0 12px 0 0",
                          height: "56px",
                          width: "70px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        dropdownStyle={{
                          borderRadius: "12px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <style dangerouslySetInnerHTML={{
                        __html: `
                          .react-tel-input .selected-flag {
                            width: 100% !important;
                            padding: 0 !important;
                            display: flex !important;
                            align-items: center !important;
                            justify-content: center !important;
                          }
                          .react-tel-input .selected-flag .dial-code {
                            display: none !important;
                          }
                        `
                      }} />
                    </div>

                    {/* Country Code Display - Separate Section */}
                    <div
                      style={{
                        backgroundColor: "#F9FAFB",
                        border: "1px solid #E4E7EC",
                        borderLeft: "none",
                        borderRight: "none",
                        height: "56px",
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        minWidth: "60px",
                        justifyContent: "center",
                        fontSize: "14px",
                        color: "#1d1d1d",
                        fontWeight: "400",
                      }}
                    >
                      +{phoneCode || "91"}
                    </div>

                    {/* Phone Number Input - Separate Section */}
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setPhone(value);
                      }}
                      placeholder="Enter phone number"
                      style={{
                        flex: 1,
                        height: "56px",
                        borderRadius: "0 12px 12px 0",
                        border: "1px solid #E4E7EC",
                        borderLeft: "none",
                        fontSize: "14px",
                        backgroundColor: "white",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        outline: "none",
                        fontFamily: "inherit",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#E4E7EC";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#E4E7EC";
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CustomTabPanel>
        </form>

        <CustomTabPanel index={0} value={value}>
          <div className="flex remember_me">
            <FormGroup>
              <FormControlLabel
                className="v2 "
                color="primary"
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => {
                      setRememberMe(e.target.checked);
                      // If user unchecks remember me, clear stored credentials immediately
                      if (!e.target.checked) {
                        removeFromStorage(STORAGE_KEYS.rememberedCredentials);
                        // Clear both email and password fields for immediate visual feedback
                        formik.setFieldValue("email", "");
                        formik.setFieldValue("password", "");
                      }
                    }}
                  />
                }
                label="Remember me"
              />
            </FormGroup>

            <Box
              component="a"
              className="text_btn"
              onClick={() => setOpen1(true)}
            >
              Forgot Password?
            </Box>
          </div>
          <div className="btn_flex">
            <Button
              type="button"
              className="w_100 br_15"
              disabled={isLoading}
              onClick={() => formik.handleSubmit()}
            >
              {isLoading ? "Please wait..." : "Login"}
            </Button>
          </div>
        </CustomTabPanel>
        <CustomTabPanel index={1} value={value}>
          {" "}
          <div className="btn_flex mt_30">
            <Button
              className="w_100 br_15"
              disabled={isPhoneLoggingIn}
              onClick={async () => {
                const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken) || "web";
                const body = {
                  fcm_token: fcmToken,
                  phone,
                  phone_code: phoneCode,
                  phone_country: phoneCountry,
                };
                try {
                  const res: any = await loginWithPhone({ body }).unwrap();

                  // Check if response indicates OTP is required (statusCode 201 typically means OTP sent)
                  if (res?.statusCode === 201 || res?.data?.user_id) {
                    // Phone login requires OTP verification first (similar to sign-up)
                    setPhoneLoginUserId(res?.data?.user_id);
                    toast.success("OTP sent to your phone");
                    setOpen2(true); // Open OTP modal
                    setCountDown(59); // Reset countdown
                    setOtp(""); // Clear OTP input
                    return;
                  }

                  // Extract token from login response - check all possible locations
                  // Try multiple nested paths to find the token
                  let token =
                    (res?.data as any)?.auth_token ||
                    (res?.data as any)?.token ||
                    (res?.data as any)?.access_token ||
                    (res?.data as any)?.accessToken ||
                    (res as any)?.token ||
                    (res as any)?.auth_token ||
                    res?.token ||
                    (res?.data?.data as any)?.auth_token ||
                    (res?.data?.data as any)?.token ||
                    (res?.data?.user as any)?.auth_token ||
                    (res?.data?.user as any)?.token;

                  // If no token found, log the response for debugging
                  if (!token) {
                    console.error("Phone login - No token found in response:", res);
                    console.error("Response keys:", Object.keys(res || {}));
                    console.error("Response data keys:", res?.data ? Object.keys(res.data) : "No data");

                    // Show user-friendly error with API message if available
                    const errorMessage = res?.message || res?.data?.message || "No authentication token received";
                    toast.error(`Login failed: ${errorMessage}`);
                    return;
                  }

                  // Save token in Redux (persisted) and localStorage for future use
                  dispatch(setToken({ token }));
                  setToStorage(STORAGE_KEYS.token, token);

                  // Remember me is not supported for phone login - always clear any phone credentials
                  const rememberedData = getFromStorage(STORAGE_KEYS.rememberedCredentials);
                  if (rememberedData) {
                    try {
                      const parsed = JSON.parse(rememberedData);
                      // Only keep email credentials, remove phone credentials
                      if (parsed.phone) {
                        if (parsed.email) {
                          // Keep only email credentials
                          setToStorage(STORAGE_KEYS.rememberedCredentials, JSON.stringify({
                            email: parsed.email,
                            password: parsed.password || "",
                          }));
                        } else {
                          // No email credentials, remove all
                          removeFromStorage(STORAGE_KEYS.rememberedCredentials);
                        }
                      }
                    } catch (error) {
                      // Ignore parsing errors
                    }
                  }

                  // Persist full user data so profile screens can use it - same as email login
                  if (res?.data) {
                    try {
                      // Include phone data in credentials for consistency
                      const credentialsData = { ...res.data };
                      if (phone) {
                        credentialsData.phone = phone;
                        credentialsData.phone_code = phoneCode;
                        credentialsData.phone_country = phoneCountry;
                      }
                      setToStorage(
                        STORAGE_KEYS.credentials,
                        JSON.stringify(credentialsData)
                      );

                      // Dispatch custom event to notify Header component of credentials update
                      if (typeof window !== "undefined") {
                        window.dispatchEvent(new Event('credentialsUpdated'));
                      }
                    } catch (error) {
                      // ignore JSON/stringify issues
                    }
                  }

                  toast.success(res?.message || "Logged in successfully");

                  // Navigate based on profile_setup status
                  // profile_setup = 0 means incomplete profile, navigate to profile setup
                  // profile_setup = 1 means profile completed, navigate to home
                  const profileSetup = res?.data?.profile_setup;
                  if (profileSetup === 0 || profileSetup === "0") {
                    router.push("/auth/profile-setup");
                  } else {
                    router.push("/");
                  }
                } catch (error: any) {
                  toast.error(error?.data?.message || "Phone login failed");
                }
              }}
            >
              {isPhoneLoggingIn ? "Please wait..." : "Login"}
            </Button>
          </div>
        </CustomTabPanel>
        <div className="or_box text_center">
          <p className="or">Or login with</p>
          <div className="btn_flex social_login ">
            <Button>
              {" "}
              <img src="/images/google_icon.svg" alt="img" />
              Google
            </Button>
            <Button>
              {" "}
              <img src="/images/fb_icon.svg" alt="img" /> Facebook
            </Button>
            <Button>
              {" "}
              <img src="/images/apple_icon.svg" alt="img" /> Apple
            </Button>
          </div>
          <p>
            By signing up, you agree to the <a> Terms of Service</a> and{" "}
            <a> Data Processing Agreement</a>
          </p>
        </div>
      </div>

      <ResetPassword
        open={open1}
        onClose={handleCloseModal1}
        setOpen={setOpen1}
      />
      <OtpVerifyModal
        open={open2}
        onClose={handleCloseModal2}
        setOpen={setOpen2}
        title="Verify Phone Number"
        description="A verification OTP has been sent to your phone. Please check your messages."
        countDown={countDown}
        setCountDown={setCountDown}
        otp={otp}
        setOtp={setOtp}
        loading={isOtpVerifying}
        onSubmit={async () => {
          // Handle OTP verification for phone login
          const body = {
            user_id: phoneLoginUserId,
            type: ONBOARDING_TYPE.PHONE,
            otp,
          };
          try {
            const res = await verifyOtp({ body }).unwrap();

            // Extract token from OTP verification response
            const token =
              (res?.data as any)?.auth_token ||
              (res?.data as any)?.token ||
              (res?.data as any)?.access_token ||
              (res as any)?.token ||
              (res?.data as any)?.accessToken;

            // Extract Node API token (jwt_token) - same as email login
            const nodeToken = (res?.data as any)?.jwt_token || "";

            if (res?.statusCode === 200) {
              if (token) {
                // Save token in Redux (persisted) and localStorage
                dispatch(setToken({ token }));
                setToStorage(STORAGE_KEYS.token, token);
                // Also save nodeToken for Node API authentication
                setToStorage(STORAGE_KEYS.tokenNode, nodeToken);
              }

              // Remember me is not supported for phone login - always clear any phone credentials
              const rememberedData = getFromStorage(STORAGE_KEYS.rememberedCredentials);
              if (rememberedData) {
                try {
                  const parsed = JSON.parse(rememberedData);
                  // Only keep email credentials, remove phone credentials
                  if (parsed.phone) {
                    if (parsed.email) {
                      // Keep only email credentials
                      setToStorage(STORAGE_KEYS.rememberedCredentials, JSON.stringify({
                        email: parsed.email,
                        password: parsed.password || "",
                      }));
                    } else {
                      // No email credentials, remove all
                      removeFromStorage(STORAGE_KEYS.rememberedCredentials);
                    }
                  }
                } catch (error) {
                  // Ignore parsing errors
                }
              }

              // Store credentials including phone data
              if (res?.data) {
                try {
                  const credentialsData = { ...res.data };
                  if (phone) {
                    credentialsData.phone = phone;
                    credentialsData.phone_code = phoneCode;
                    credentialsData.phone_country = phoneCountry;
                  }
                  setToStorage(
                    STORAGE_KEYS.credentials,
                    JSON.stringify(credentialsData)
                  );

                  // Dispatch custom event to notify Header component
                  if (typeof window !== "undefined") {
                    window.dispatchEvent(new Event('credentialsUpdated'));
                  }
                } catch (error) {
                  // ignore JSON/stringify issues
                }
              }

              setOpen2(false);
              toast.success("OTP Verified. Logged in successfully");

              // Delay to ensure Redux state is persisted
              await new Promise(resolve => setTimeout(resolve, 300));

              // Force router refresh
              router.refresh();

              // Navigate based on profile_setup status
              const profileSetup = res?.data?.profile_setup;
              if (profileSetup === 0 || profileSetup === "0") {
                router.push("/auth/profile-setup");
              } else {
                router.push("/");
              }
            }
          } catch (error: any) {
            toast.error(error?.data?.message || "OTP verification failed");
          }
        }}
        onResend={async () => {
          // Resend OTP for phone login
          const body = {
            user_id: phoneLoginUserId,
            type: ONBOARDING_TYPE.PHONE,
          };
          try {
            const res = await resendOtp({ body }).unwrap();
            if (res?.statusCode === 200) {
              toast.success("OTP resent to your phone");
              setCountDown(59);
              setOtp("");
            }
          } catch (error: any) {
            toast.error(error?.data?.message || "Failed to resend OTP");
          }
        }}
      />
    </>
  );
};

export default Login;
